/**
 * `RecommendationStack` — the bottom-anchored row of `RecommendationCard`s
 * driven by `useRecommendations()`.
 *
 * Responsibilities:
 *
 *   1. Subscribe to `useRecommendations()` and render one card per entry.
 *   2. Animate cards **in** on mount (slide up + fade) so the recs feel like
 *      they "land" on top of the scene rather than blink in.
 *   3. Animate cards **out** when their `id` disappears from the recs list.
 *      The exit is celebratory: a torch-gold check overlay flashes inside the
 *      card before the card slides up and fades — this is the demo's "wow"
 *      before/after moment when R-001 vanishes after the user confirms the
 *      new `liability_personal` contract.
 *   4. Animate re-orders smoothly via `layoutId` on the underlying card.
 *
 * The exit-with-check effect is implemented by wrapping each card in a small
 * presence-aware `<ExitingCard>` that pulses the check overlay during its own
 * `exit` lifecycle. Total exit duration: ~600ms (~360ms check pulse +
 * ~360ms slide-up + fade overlap), tuned to be readable without slowing the
 * demo. Easing is `linear` everywhere to keep the chunky stepped feel.
 *
 * Empty state: when there are zero recommendations we return `null` so the
 * scene below remains fully clickable and the bottom of the viewport doesn't
 * get an empty band of dead space.
 */
import { AnimatePresence, motion } from 'framer-motion'
import { Check } from 'lucide-react'

import { RecommendationCard } from '@/components/RecommendationCard'
import { useRecommendations } from '@/hooks/queries'
import type { Recommendation } from '@shared/schemas'

/**
 * Total exit duration in seconds. Kept as a named constant so the wiki spec
 * and the component point at a single value.
 */
const EXIT_DURATION_S = 0.6
/** Check overlay hold window before the card starts sliding away (in seconds). */
const CHECK_HOLD_S = 0.36

/**
 * One card wrapped in its own `AnimatePresence`-aware presence node. We need
 * the wrapper because:
 *
 *   - The card itself uses `layoutId` for smooth re-order (handled in
 *     `RecommendationCard`).
 *   - The wrapper owns the celebratory check overlay shown during exit only.
 *     Putting that overlay inside the card would force it to render whenever
 *     the card mounts, including the first paint — and we only want it when
 *     a rule stops firing.
 */
function ExitingCard({ rec }: { rec: Recommendation }): JSX.Element {
  return (
    <motion.div
      // `key` is `rec.id`; setting it on the wrapper means React unmounts
      // this node when the id leaves the list, which is exactly when we
      // want the exit animation to play.
      key={rec.id}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{
        // Linear easing everywhere to feel stepped/stamped rather than easy-out.
        duration: EXIT_DURATION_S,
        ease: 'linear',
      }}
      className="relative"
    >
      <RecommendationCard rec={rec} />

      {/* Celebratory check overlay — only renders while this node is exiting.
          The overlay sits on top of the card with a `pop-covered/40` wash so
          the "rule cleared" semantic reads at a glance, and the inner check
          chip uses torch-gold + a chunky silhouette border. */}
      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        exit={{
          opacity: [0, 1, 1, 0],
          scale: [0.6, 1.1, 1, 1],
          transition: {
            duration: EXIT_DURATION_S,
            times: [0, 0.2, CHECK_HOLD_S / EXIT_DURATION_S, 1],
            ease: 'linear',
          },
        }}
        className="pointer-events-none absolute inset-0 flex items-center justify-center bg-pop-covered/40"
      >
        <div className="flex h-10 w-10 items-center justify-center bg-pop-torch border-[3px] border-pop-silhouette shadow-pixel-sm">
          <Check className="h-6 w-6 text-pop-silhouette" strokeWidth={3} />
        </div>
      </motion.div>
    </motion.div>
  )
}

export function RecommendationStack(): JSX.Element | null {
  const { data } = useRecommendations()
  const recs = data ?? []

  if (recs.length === 0) return null

  return (
    <div
      // Fixed at the bottom edge, centred horizontally. `pointer-events-none`
      // on the outer wrapper would block the cards too — so we only pad and
      // position here; clickability is preserved on the inner row.
      className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center pb-6"
      aria-label="Doporučení"
    >
      <div
        // `pop-scrollbar` styles the horizontal scroll polish to match the
        // chunky palette (sand-dark track, silhouette thumb).
        className="pop-scrollbar pointer-events-auto flex max-w-full gap-3 overflow-x-auto px-4 pb-1"
      >
        <AnimatePresence mode="popLayout" initial={true}>
          {recs.map((rec) => (
            <ExitingCard key={rec.id} rec={rec} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default RecommendationStack
